import { useRef, useEffect } from 'react';
import { FooterNewsletter } from 'Shared/newsletter';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { FaIcon } from '@ifixit/icons';
import { faEye } from '@fortawesome/pro-solid-svg-icons/faEye';
import { useHideSubBox } from 'Shared/newsletter_shared_state';
import type { NewsletterFormProps } from '@ifixit/footer/components/Newsletter';
import { _js } from '@ifixit/localize';

function NewsletterInput({ placeholder, label }: { placeholder: string; label: string }) {
   return (
      <div className="w-full">
         <input
            type="email"
            className="w-full h-10 px-4 bg-white text-gray-900 text-sm rounded border border-gray-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
            placeholder={placeholder}
            aria-label={label}
         />
      </div>
   );
}

function NewsletterHeader({ title, subtitle }: { title: string; subtitle: string }) {
   return (
      <div className="text-left">
         <p className="font-bold text-lg text-white mb-2 mt-0">{title}</p>
         <p className="text-gray-300 text-sm font-normal mt-3">{subtitle}</p>
      </div>
   );
}

function NewsletterForm({
   placeholder,
   buttonText,
   label,
}: {
   placeholder: string;
   buttonText: string;
   label: string;
}) {
   const [hideSubBox] = useHideSubBox();
   if (hideSubBox) {
      return null;
   }
   return (
      <form
         className="flex max-lg:flex-wrap gap-3 mt-3 w-full xl:w-auto justify-center md:justify-end items-start"
         data-testid="footer-newsletter-form"
      >
         <NewsletterInput placeholder={placeholder} label={label} />
         <button
            type="submit"
            className="bg-brand-500 text-white border-none h-10 px-4 rounded font-semibold shrink-0 w-full md:w-full lg:w-auto hover:bg-brand-600 transition-colors"
            data-testid="footer-newsletter-subscribe-button"
         >
            {buttonText}
         </button>
      </form>
   );
}

function NewsletterSubscribed() {
   const [hideSubBox] = useHideSubBox();
   if (!hideSubBox) {
      return null;
   }

   return (
      <div className="h-[42px] flex flex-row items-center p-3 gap-2 w-full bg-[#EDF6FF] border border-[#77B5FF] rounded !mt-0">
         <FaIcon icon={faCircleCheck} className="text-[#1975F1]" />
         <p className="text-gray-900 m-0 text-base">{_js('Subscribed!')}</p>
      </div>
   );
}

function NewsletterLink() {
   const [hideSubBox] = useHideSubBox();
   const message = hideSubBox ? _js('Let me read it!') : _js('Let me read it first!');
   return (
      <a
         href="/Newsletter"
         className="flex items-center text-gray-300 mt-2 mr-auto underline hover:text-white visited:text-gray-300"
      >
         <FaIcon icon={faEye} className="inline mr-1 align-[-3px]" />
         {message}
      </a>
   );
}

export function NewsletterComponent({
   newsletterForm,
}: {
   newsletterForm: NewsletterFormProps;
}) {
   const ref = useRef<HTMLDivElement>(null);
   useEffect(() => {
      if (ref.current) {
         new FooterNewsletter(ref.current);
      }
   }, []);

   return (
      <div ref={ref} className="flex flex-col mt-4 md:mt-0 w-full md:w-64 lg:w-96">
         <NewsletterHeader title={newsletterForm.title} subtitle={newsletterForm.subtitle} />
         <NewsletterForm
            placeholder={newsletterForm.inputPlaceholder}
            buttonText={newsletterForm.callToActionButtonTitle}
            label={newsletterForm.title}
         />
         <NewsletterSubscribed />
         <NewsletterLink />
      </div>
   );
}
